import { AutoComplete, AutoCompleteProps } from "antd";
import { FC, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { LocationIcon, RoomIcon } from "src/assets/Icons";
import { FloatingLabelOptions, withFloatingLabel } from "src/HOC/WithFloatingLabel";
import { HotelSelectOptionType, mapOption } from "../utils";
import PulseLoadingIndicator from "src/components/PulseLoadingIndicator/PulseLoadingIndicator";
import { useToast } from "src/context";

const FloatingLocationSelectionWithLabel = withFloatingLabel(AutoComplete);

type HotelSelectWebProps = FloatingLabelOptions &
  AutoCompleteProps & {
    options?: ReturnType<typeof mapOption>[];
    isFetching?: boolean;
    searchValue?: string;
    onSearch?: (value: string) => void;
    error: any;
    validationError: any;
  };

const HotelSelectWeb: FC<HotelSelectWebProps> = ({
  onChange,
  value,
  options = [],
  isFetching,
  searchValue,
  onSearch,
  error,
  validationError,
  ...props
}) => {
  const text = searchValue ?? (value as HotelSelectOptionType | undefined)?.label ?? "";
  const selectOptions = options.map((option) => ({ ...option, code: option.value, value: `${option.type}-${option.value}` }));
  const { t } = useTranslation();
  const toast = useToast();
  useEffect(() => {
    if (isFetching || !error) return;
    toast.error(t("Hotels.search.noHotelsOrCitiesFound"));
  }, [error, isFetching]);

  return (
    <FloatingLocationSelectionWithLabel
      value={text}
      float={Boolean(text)}
      blurOnChange={false}
      showSearch={{
        onSearch,
        filterOption: true,
        optionFilterProp: "label",
        searchValue,
      }}
      suffix={<PulseLoadingIndicator show={isFetching} running={isFetching} className={"bg-background-primary-lighter!"} />}
      notFoundContent={null}
      popupRender={(menu) => {
        return (
          <div className="min-w-70! md:min-w-100! w-full! [&_.rc-virtual-list]:w-full! [&_.rc-virtual-list-holder]:scrollbar-hide! flex items-center justify-center">
            {menu}
          </div>
        );
      }}
      placement="bottomLeft"
      popupMatchSelectWidth={false}
      options={selectOptions}
      onSelect={(_, option: any) => {
        onChange?.({
          value: option.code,
          label: option.label,
          type: option.type,
          subLabel: option.subLabel,
          country: option.country,
        });
      }}
      classNames={{
        popup: {
          list: "w-full!",
          root: "w-full!",
          listItem: "w-full!",
        },
        root: "w-full!",
        label: "text-font-minimal!",
      }}
      optionRender={(option) => {
        const optionData = option.data as HotelSelectOptionType;
        const isHotelOption = optionData.type === "hotels";

        return (
          <div className="w-full! hover:text-font-active flex min-w-min items-center gap-1 p-1 px-5">
            <div className="flex h-1 w-1 flex-1 items-center justify-center font-bold">
              {isHotelOption ? <RoomIcon className="h-4 w-4" /> : <LocationIcon className="h-4 w-4" />}
            </div>
            <div className="flex-9 text-font-default hover:text-font-default flex flex-col items-start justify-center whitespace-break-spaces">
              <span className="font-semibold">{optionData.label}</span>
              <span className="text-font-minimal">{optionData.subLabel}</span>
            </div>
          </div>
        );
      }}
      {...props}
    ></FloatingLocationSelectionWithLabel>
  );
};

export default HotelSelectWeb;
